import { Environment } from "@lyfegen-platform/graphql-schema/models";
import { dataModuleString, Loading } from "@lyfegen-platform/react-common";
import { Box } from "@material-ui/core";
import { decode } from "js-base64";
import { identity, unionBy } from "lodash";
import { lazy, Suspense, useEffect } from "react";
import { Redirect } from "react-router";
import { Route, Switch } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import LoadingBox from "../components/LoadingBox";
import NoModule from "../components/NoModules";
import { SENTRY_ENV_NOT_SET } from "../config";
import { getAgreementTableColumns } from "../containers/agreements/AgreementsTable";
import { getTransactionInDocumentTableColumns } from "../containers/financials/documents/invoices/refunds/RefundsTable";
import { getRefundColumns } from "../containers/financials/refunds/columns/useRefundColumns";
import { getRecordColumns } from "../containers/data/tables/columns/useRecordColumns";
import { getFormColumns } from "../containers/data/tables/columns/useFormColumns";
import { GET_CORE_CONFIG_DATA } from "../containers/home/queries";
import { GetCoreConfigurationQuery } from "../containers/home/types/queries.generated";
import ErrorPage from "../containers/layout/ErrorPage";
import Layout from "../containers/layout/Layout";
import { simulationGroupTableColumns } from "../containers/simulator/views/GroupView";
import { simulationTableColumns } from "../containers/simulator/views/ListView";
import { CreateDomain } from "../containers/user/organization-settings/domains/CreateDomain";
import { UpdateDomain } from "../containers/user/organization-settings/domains/UpdateDomain";
import OrganizationSettings from "../containers/user/organization-settings/OrganizationSettings";
import { GET_LIBRARY_USER_DOMAINS, GET_USER_PROFILE_INFO_DATA } from "../containers/user/queries";
import { useDataQuery } from "../hooks/useDataModule";
import { ModulesEnum, useModuleRouteData } from "../models/common/modules";
import { CoreConfigurationData, Country, DocumentType } from "../models/generated/graphql";
import { OtpToken } from "../models/identity/otp-token";
import IUserDataProps from "../models/identity/user-data";
import { AuthenticationService } from "../services/authentication.service";
import { coreConfig } from "../store/core-config";
import { environmentState, userOrganization } from "../store/layout";
import { useSetUserFromUserDataProps } from "../store/user";
import useUrlQuery from "../utils/use-url-query";
import Key11Routes from "./module1/Key11Routes";
import Key21Routes from "./module2/Key21Routes";
import Key31Routes from "./module3/Key31Routes";
import Key41Routes from "./module4/Key41Routes";
import Key51Routes from "./module5/Key51Routes";
import Key52Routes from "./module5/Key52Routes";
import Key81Routes from "./module8/Key81Routes";
import { GetLibraryUserDomainsQuery, GetUserProfileInfoDataQuery } from "../containers/user/types/queries.generated";
import { modelsModuleString } from "../hooks/useModelsModule";
import { useBatchQuery } from "../hooks/useBatchQuery";
import { getDocumentColumns } from "../containers/financials/documents/utils/useDocumentColumns";
import { useTranslation } from "react-i18next";

const otpTokenParam = "otp-token";

const NotFound = lazy(() => import("../components/NotFound"));
const UserHome = lazy(() => import("../containers/user/UserHome"));
const UpdateInvoice = lazy(() => import("../containers/financials/documents/invoices/UpdateRoot"));
const RefundInvoice = lazy(() => import("../containers/financials/documents/invoices/refund-invoice-document/RefundInvoice"));

function AutomaticSignOutComponent(props: { authService: AuthenticationService }) {
	useEffect(() => {
		props.authService.startSignOut();
	}, []);

	return <></>;
}

export default function MainRoutes(props: {
	user: IUserDataProps;
	authService: AuthenticationService;
	handleLogout: () => void;
	identityServerUrl: string;
	serverEnvironment: Environment;
	sentryEnv: Environment | typeof SENTRY_ENV_NOT_SET;
}) {
	const setUser = useSetUserFromUserDataProps();
	const setCoreConfig = useSetRecoilState(coreConfig);
	const { firstAvailableModule, modulesData } = useModuleRouteData(props.user);
	const { t } = useTranslation();
	const userOrg = useRecoilValue(userOrganization);
	const params = useUrlQuery();
	const { user, authService } = props;
	const token: OtpToken = params.get(otpTokenParam) && JSON.parse(decode(decodeURIComponent(params.get(otpTokenParam)!)));
	const setEnvironment = useSetRecoilState(environmentState);
	const needsLibraryUserData = user.modules.some((m) => m.code === ModulesEnum.library || m.code === ModulesEnum.simulator);
	const { data, loading } = useBatchQuery<[GetUserProfileInfoDataQuery, GetLibraryUserDomainsQuery]>(
		[
			{
				query: GET_USER_PROFILE_INFO_DATA,
				variables: { input: { userId: props.user.id, isQuerySourceImage: true } },
				context: { module: dataModuleString },
			},
			needsLibraryUserData
				? {
						query: GET_LIBRARY_USER_DOMAINS,
						variables: { userId: props.user.id },
						context: { module: modelsModuleString },
				  }
				: null,
		].filter(Boolean) as any
	);

	const [userProfile, libraryUser] = data || [];

	const {
		data: coreConfigData,
		loading: coreConfigDataLoading,
		error: coreConfigError,
	} = useDataQuery<GetCoreConfigurationQuery>(GET_CORE_CONFIG_DATA);

	useEffect(() => {
		if (token && props.user.exposedResource?.id !== token.Id) {
			props.authService.startSignOut({ post_logout_redirect_uri: document.location.href });
		}
		setEnvironment(props.sentryEnv || SENTRY_ENV_NOT_SET);
	}, []);

	useEffect(() => {
		if (!userProfile?.GetUserProfileInfoData) {
			return;
		}

		if (needsLibraryUserData && !libraryUser?.users?.[0]) {
			return;
		}

		setUser({
			...props.user,
			domains: libraryUser?.users[0].domainWithChildren.map((d) => ({ id: d.id, level: d.level, name: d.name })) || [],
			userImg: userProfile.GetUserProfileInfoData.editedUserPicture,
			sourceUserImage: userProfile?.GetUserProfileInfoData.sourceUserPicture,
			sourceUserSignature: userProfile?.GetUserProfileInfoData.sourceUserSignature,
			userSignature: userProfile?.GetUserProfileInfoData.editedUserSignature,
			isSignatureCreationDraw: userProfile?.GetUserProfileInfoData.isSignatureCreationDraw,
			notificationCount: userProfile?.GetUserProfileInfoData.notificationCount || 0,
			questsCount: userProfile?.GetUserProfileInfoData.questsCount || 0,
			agreementsTableColumns: !userProfile?.GetUserProfileInfoData?.agreementsTableColumns?.length
				? getAgreementTableColumns()
						.filter((col) => col.default)
						.map((col) => col.field)
				: userProfile?.GetUserProfileInfoData.agreementsTableColumns,
			transactionsInRefundInvoiceDocumentTableColumns: !userProfile?.GetUserProfileInfoData
				?.transactionsInRefundInvoiceDocumentTableColumns?.length
				? getTransactionInDocumentTableColumns([], DocumentType.Invoice, t)
						.filter((col) => col.default)
						.map((col) => col.field)
				: userProfile?.GetUserProfileInfoData.transactionsInRefundInvoiceDocumentTableColumns,
			transactionsInCreditNoteDocumentTableColumns: !userProfile?.GetUserProfileInfoData?.transactionsInCreditNoteDocumentTableColumns
				?.length
				? getTransactionInDocumentTableColumns([], DocumentType.CreditNote, t)
						.filter((col) => col.default)
						.map((col) => col.field)
				: userProfile?.GetUserProfileInfoData.transactionsInCreditNoteDocumentTableColumns,
			transactionsInFreeOfChargeDocumentTableColumns: !userProfile?.GetUserProfileInfoData
				?.transactionsInFreeOfChargeDocumentTableColumns?.length
				? getTransactionInDocumentTableColumns([], DocumentType.FreeOfCharge, t)
						.filter((col) => col.default)
						.map((col) => col.field)
				: userProfile?.GetUserProfileInfoData.transactionsInFreeOfChargeDocumentTableColumns,
			simulationsTableColumns: !libraryUser?.users[0]?.simulationsTableColumns?.length
				? unionBy(simulationGroupTableColumns, simulationTableColumns, (column) => column.field)
						.filter((col) => col.default)
						.map((col) => col.field)
				: libraryUser?.users[0].simulationsTableColumns,
			transactionsTableColumns: !userProfile?.GetUserProfileInfoData?.transactionsTableColumns?.length
				? getRefundColumns(identity, { hasOnlyPatientAgreements: false, hasOnlyCaselessAgreements: false })
						.filter((col) => col.default)
						.map((col) => col.field as string)
				: userProfile?.GetUserProfileInfoData.transactionsTableColumns,
			recordsTableColumns: !userProfile?.GetUserProfileInfoData?.recordsTableColumns?.length
				? getRecordColumns(identity, false, false, "date")
						.filter((col) => col.default)
						.map((col) => col.field as string)
				: userProfile?.GetUserProfileInfoData.recordsTableColumns,
			formsTableColumns: !userProfile?.GetUserProfileInfoData?.formsTableColumns?.length
				? getFormColumns(identity)
						.filter((col) => col.default)
						.map((col) => col.field as string)
				: userProfile?.GetUserProfileInfoData.formsTableColumns,
			documentsTableColumns: !userProfile?.GetUserProfileInfoData?.documentsTableColumns?.length
				? getDocumentColumns(identity)
						.filter((col) => col.default)
						.map((col) => col.field as string)
				: userProfile?.GetUserProfileInfoData.documentsTableColumns,
			organization: props.user.organization && {
				...props.user.organization,
				country: userProfile.GetUserProfileInfoData.country as Country,
			},
			simulatorWasteReportingEnabled: libraryUser?.users[0]?.simulatorWasteReportingEnabled ?? true,
			defaultMessageSharingType: userProfile?.GetUserProfileInfoData?.defaultMessageSharingType ?? undefined,
		});
	}, [userProfile, libraryUser, props.user, needsLibraryUserData]);

	if (loading || coreConfigDataLoading) {
		return (
			<Box height="95vh" display="flex" justifyContent="center" alignItems="center">
				<LoadingBox />
			</Box>
		);
	}

	coreConfigData && !coreConfigError && setCoreConfig(coreConfigData?.GetCoreConfiguration as CoreConfigurationData);

	return (
		<Layout
			modules={modulesData}
			isUserGuest={user.isGuest || !!user.exposedResource}
			handleLogout={props.handleLogout}
			identityServerUrl={props.identityServerUrl}
			serverEnvironment={props.serverEnvironment}
		>
			<Suspense
				fallback={
					<Box height="95vh" display="flex" justifyContent="center" alignItems="center">
						<Loading />
					</Box>
				}
			>
				<Switch>
					{modulesData.map((m) =>
						m.subModules.map((sm) => {
							if (sm.key === "1.1") {
								return Key11Routes(sm.key, sm.link);
							}
							if (sm.key === "2.1") {
								return Key21Routes(sm.key, sm.link);
							}
							if (sm.key === "3.1") {
								return Key31Routes(sm.key, sm.link);
							}
							if (sm.key === "4.1") {
								return Key41Routes(sm.key, sm.link, props.identityServerUrl);
							}
							if (sm.key === "5.1") {
								return Key51Routes(sm.key, sm.link);
							}
							if (sm.key === "5.2") {
								return Key52Routes(sm.key, sm.link);
							}
							if (sm.key === "8.1") {
								return Key81Routes(sm.key, sm.link, props.serverEnvironment);
							}
							return null;
						})
					)}
					{!props.user.exposedResource && (
						<Route exact path="/">
							<Redirect to={"/" + firstAvailableModule} />
						</Route>
					)}
					<Route
						path="/organization-settings/domains/:domain/create-domain"
						component={(props: any) => <CreateDomain {...props} />}
					/>
					<Route path="/organization-settings/domains/:domain" component={(props: any) => <UpdateDomain {...props} />} />
					<Route path="/organization-settings/:tab">{userOrg && userOrg.id && <OrganizationSettings userOrg={userOrg} />}</Route>
					<Route path="/organization-settings">{userOrg && userOrg.id && <OrganizationSettings userOrg={userOrg} />}</Route>
					<Route path="/profile-settings">
						<UserHome identityServerUrl={props.identityServerUrl} authService={props.authService} />
					</Route>
					<Route path="/error">
						<ErrorPage />
					</Route>
					<Route path="/noModule">
						<Route component={NoModule} />
					</Route>
					<Route path="/not-found">
						<Route component={NotFound} />
					</Route>
					<Route path="/models">
						<Redirect to={"/library"} />
					</Route>
					<Route path="/document" component={(props: any) => <UpdateInvoice {...props} />}></Route>
					<Route
						path="/financials/documents/invoice/print/:invoiceId"
						component={(props: any) => <RefundInvoice {...props} />}
					></Route>
					<Route
						component={(props: any) =>
							user?.exposedResource ? (
								<AutomaticSignOutComponent {...props} authService={authService} />
							) : (
								<NotFound {...props} />
							)
						}
					/>
				</Switch>
			</Suspense>
		</Layout>
	);
}
